/**
 * A radial tool menu that opens at the pointer on Q (see
 * {@link PointerMenu}). While Q is held the direction alone picks, so a
 * flick past the rim still lands; a click must land on the pie.
 */
import { Box, Typography, alpha, keyframes, useTheme } from '@mui/material';
import PointerMenu from '~/components/toolbar/pointerMenu/PointerMenu';
import {
    hotkeyLabel,
    type ToolEntry,
} from '~/components/toolbar/shared/toolEntry';
import {
    clampCenter,
    polar,
    sectorAt,
    sectorCenterAngle,
    sectorSpan,
    wedgePath,
} from './radialGeometry';

const OPEN_KEY = 'q';
/** How far past the rim a click still picks a wedge in an open menu. */
const CLICK_SLACK = 14;
/** The hub's radius; a release or click inside it picks nothing. */
const PIE_INNER_RADIUS = 40;
const PIE_OUTER_RADIUS = 156;
/** The room under the pie that the refusal strip takes. */
const PIE_STRIP_ROOM = 48;

const fadeIn = keyframes`
    from { opacity: 0; transform: scale(0.92); }
    to { opacity: 1; transform: scale(1); }
`;

/** A key drawn as a keycap. */
function Keycap({ hotkey }: { hotkey: string }) {
    const theme = useTheme();
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 16,
                height: 16,
                px: 0.5,
                borderRadius: 0.5,
                border: `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
                borderBottomWidth: 2,
                fontFamily: 'monospace',
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
                color: theme.palette.common.white,
                bgcolor: alpha(theme.palette.common.black, 0.35),
            }}
        >
            {hotkeyLabel(hotkey)}
        </Box>
    );
}

/** The entry's icon at a fixed size, tinted when the tool is armed. */
function EntryIcon({ entry, size = 22 }: { entry: ToolEntry; size?: number }) {
    const theme = useTheme();
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                color: entry.selected
                    ? theme.palette.primary.main
                    : theme.palette.common.white,
                '& svg': { width: size, height: size },
            }}
        >
            {entry.icon}
        </Box>
    );
}

/** Under the pie: why the hovered tool is refused, or its usage hint. */
function EntryStrip({ entry }: { entry: ToolEntry }) {
    const theme = useTheme();
    const text = entry.disabled ? entry.disabledReason : entry.hint;
    if (!text) {
        return null;
    }
    return (
        <Box
            sx={{
                position: 'absolute',
                left: 0,
                top: PIE_OUTER_RADIUS + 10,
                transform: 'translateX(-50%)',
                width: 'max-content',
                maxWidth: 320,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.background.paper, 0.94),
                border: `1px solid ${theme.palette.divider}`,
                textAlign: 'center',
                pointerEvents: 'none',
            }}
        >
            <Typography
                fontSize={11}
                sx={
                    entry.disabled
                        ? { color: theme.palette.warning.main }
                        : { opacity: 0.7 }
                }
            >
                {text}
            </Typography>
        </Box>
    );
}

interface RadialMenuPieProps {
    /** The tools, clockwise from the top. */
    entries: readonly ToolEntry[];
    /** The sector under the pointer, or null in the hub. */
    hovered: number | null;
}

/**
 * The pie, drawn around (0, 0), the menu's centre: solid wedges out from a
 * small hub, each holding the tool's icon and name, with its key by the
 * hub. The armed tool's wedge carries an arc on its rim, and its icon sits
 * in the hub.
 */
function RadialMenuPie({ entries, hovered }: RadialMenuPieProps) {
    const theme = useTheme();
    const current = entries.find((e) => e.selected);
    const inner = PIE_INNER_RADIUS;
    const outer = PIE_OUTER_RADIUS;
    const span = sectorSpan(entries.length);
    const size = outer * 2 + 4;
    return (
        <Box
            sx={{ position: 'absolute', animation: `${fadeIn} 90ms ease-out` }}
        >
            <svg
                width={size}
                height={size}
                viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`}
                style={{
                    position: 'absolute',
                    left: -size / 2,
                    top: -size / 2,
                }}
            >
                {entries.map((entry, i) => {
                    const mid = sectorCenterAngle(i, entries.length);
                    const from = mid - span / 2;
                    const to = mid + span / 2;
                    const highlight = entry.disabled
                        ? theme.palette.warning.main
                        : theme.palette.primary.main;
                    return (
                        <g key={entry.id}>
                            <path
                                d={wedgePath(inner, outer, from, to)}
                                fill={
                                    hovered === i
                                        ? alpha(highlight, 0.35)
                                        : alpha(
                                              theme.palette.background.paper,
                                              0.9
                                          )
                                }
                                stroke={alpha(theme.palette.common.black, 0.7)}
                                strokeWidth={2}
                            />
                            {entry.selected && (
                                <path
                                    d={wedgePath(
                                        outer - 4,
                                        outer,
                                        from + 0.03,
                                        to - 0.03
                                    )}
                                    fill={theme.palette.primary.main}
                                />
                            )}
                        </g>
                    );
                })}
                <circle
                    r={inner - 3}
                    fill={alpha(theme.palette.background.paper, 0.95)}
                    stroke={alpha(theme.palette.common.white, 0.15)}
                />
            </svg>
            {entries.map((entry, i) => {
                const mid = sectorCenterAngle(i, entries.length);
                const body = polar((inner + outer) / 2 + 8, mid);
                const key = polar(inner + 14, mid);
                const opacity = entry.disabled ? 0.38 : 1;
                return (
                    <Box key={entry.id} sx={{ pointerEvents: 'none' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: body.x,
                                top: body.y,
                                transform: 'translate(-50%, -50%)',
                                width: 84,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                                opacity,
                            }}
                        >
                            <EntryIcon entry={entry} />
                            <Typography
                                fontSize={11}
                                fontWeight={600}
                                lineHeight={1.15}
                                textAlign="center"
                            >
                                {entry.label}
                            </Typography>
                        </Box>
                        {entry.hotkey && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: key.x,
                                    top: key.y,
                                    transform: 'translate(-50%, -50%)',
                                    display: 'flex',
                                    opacity,
                                }}
                            >
                                <Keycap hotkey={entry.hotkey} />
                            </Box>
                        )}
                    </Box>
                );
            })}
            {current && (
                <Box
                    sx={{
                        position: 'absolute',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        pointerEvents: 'none',
                    }}
                >
                    <EntryIcon entry={current} size={20} />
                </Box>
            )}
            {hovered !== null && <EntryStrip entry={entries[hovered]} />}
        </Box>
    );
}

interface RadialToolMenuProps {
    /** The tools, clockwise from the top. Eight at most reads best. */
    entries: readonly ToolEntry[];
}

export default function RadialToolMenu({ entries }: RadialToolMenuProps) {
    return (
        <PointerMenu
            openKey={OPEN_KEY}
            placeAt={(pointer) =>
                clampCenter(
                    pointer,
                    PIE_OUTER_RADIUS + PIE_STRIP_ROOM,
                    window.innerWidth,
                    window.innerHeight
                )
            }
            itemAt={(at, center, gesture) =>
                sectorAt(
                    at.x - center.x,
                    at.y - center.y,
                    entries.length,
                    PIE_INNER_RADIUS,
                    gesture === 'release'
                        ? Infinity
                        : PIE_OUTER_RADIUS + CLICK_SLACK
                )
            }
            onPick={(index) => {
                entries[index]?.arm();
            }}
            data-testid="radial-tool-menu"
        >
            {(hovered) => <RadialMenuPie entries={entries} hovered={hovered} />}
        </PointerMenu>
    );
}
